import React, { useEffect, useState } from "react";
import { Box, Button, InputAdornment, TextField, Typography } from "@mui/material";
import {
  AutoAwesomeOutlined,
  PhoneInTalkOutlined,
  ReceiptLongOutlined,
  VpnKeyOutlined,
  BlockOutlined,
  FingerprintOutlined,
} from "@mui/icons-material";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import SettingsCard from "../Settings/SettingsCard";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: theme.spacing(3),
    alignItems: "start",
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
  },
  helper: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
  },
  groupTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(1),
    "&::after": {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.divider,
    },
  },
  group: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
}));

const SETTING_KEYS = {
  webhookSecret: "openaiSipWebhookSecret",
  defaultApiKey: "openaiSipDefaultApiKey",
  projectId: "openaiProjectId",
  adminApiKey: "openaiAdminApiKey",
};

const iconAdornment = (Icon) => ({
  startAdornment: (
    <InputAdornment position="start">
      <Icon fontSize="small" color="disabled" />
    </InputAdornment>
  ),
});

const OpenAiAccountSettings = () => {
  const { classes } = useStyles();

  const [webhookSecret, setWebhookSecret] = useState("");
  const [defaultApiKey, setDefaultApiKey] = useState("");
  const [projectId, setProjectId] = useState("");
  const [adminApiKey, setAdminApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/settings");
        const list = Array.isArray(data) ? data : [];
        setWebhookSecret(list.find((s) => s.key === SETTING_KEYS.webhookSecret)?.value || "");
        setDefaultApiKey(list.find((s) => s.key === SETTING_KEYS.defaultApiKey)?.value || "");
        setProjectId(list.find((s) => s.key === SETTING_KEYS.projectId)?.value || "");
        setAdminApiKey(list.find((s) => s.key === SETTING_KEYS.adminApiKey)?.value || "");
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      await api.put(`/settings/${SETTING_KEYS.webhookSecret}`, { value: webhookSecret.trim() });
      await api.put(`/settings/${SETTING_KEYS.defaultApiKey}`, { value: defaultApiKey.trim() });
      await api.put(`/settings/${SETTING_KEYS.projectId}`, { value: projectId.trim() });
      await api.put(`/settings/${SETTING_KEYS.adminApiKey}`, { value: adminApiKey.trim() });
      toast.success("Configuración de OpenAI guardada");
    } catch (err) {
      toastError(err);
    }

    setSaving(false);
  };

  return (
    <SettingsCard
      icon={AutoAwesomeOutlined}
      title="OpenAI (cuenta)"
      subtitle="Datos de TODA la cuenta de OpenAI de esta empresa -- no son por número ni por conexión puntual."
    >
    <Box className={classes.root}>
      <div className={classes.columns}>
        <div className={classes.column}>
          <Typography className={classes.groupTitle}>
            <PhoneInTalkOutlined fontSize="small" /> Llamada por teléfono
          </Typography>
          <div className={classes.group}>
            <TextField
              label="Secreto del webhook"
              size="small"
              variant="outlined"
              fullWidth
              placeholder="whsec_..."
              helperText='El que te dio OpenAI al crear el webhook de "llamada entrante" (Settings → Project → Webhooks).'
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              InputProps={iconAdornment(VpnKeyOutlined)}
            />

            <TextField
              label="API key de rechazo"
              size="small"
              variant="outlined"
              fullWidth
              placeholder="sk-..."
              helperText="Para rechazar una llamada a un número que no configuraste en ninguna conexión."
              value={defaultApiKey}
              onChange={(e) => setDefaultApiKey(e.target.value)}
              InputProps={iconAdornment(BlockOutlined)}
            />

            <TextField
              label="Project ID"
              size="small"
              variant="outlined"
              fullWidth
              placeholder="proj_..."
              helperText="Settings → Project → General en OpenAI. Completa la URI del troncal SIP."
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              InputProps={iconAdornment(FingerprintOutlined)}
            />
          </div>
        </div>

        <div className={classes.column}>
          <Typography className={classes.groupTitle}>
            <ReceiptLongOutlined fontSize="small" /> Costos de IA
          </Typography>
          <div className={classes.group}>
            <TextField
              label="Admin API key"
              size="small"
              variant="outlined"
              fullWidth
              placeholder="sk-admin-..."
              helperText="Settings → Organization → Admin keys en OpenAI, con el permiso api.usage.read. Para ver el gasto real."
              value={adminApiKey}
              onChange={(e) => setAdminApiKey(e.target.value)}
              InputProps={iconAdornment(ReceiptLongOutlined)}
            />
          </div>
        </div>
      </div>

      <Button
        variant="contained"
        color="primary"
        style={{ alignSelf: "flex-start" }}
        disabled={loading || saving}
        onClick={handleSave}
      >
        Guardar
      </Button>
    </Box>
    </SettingsCard>
  );
};

export default OpenAiAccountSettings;
